import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { Button } from 'antd';
import { CheckCircleFilled, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAndroidWelcome } from '../../lib/androidWelcome';
import { useInstallApp } from '../../lib/installApp';
import { trackConversion } from '../../lib/conversionEvents';
import { usePush } from '../Notifications/PushProvider';
import { useAppSelector } from '../../store/hooks';
import { selectUser } from '../../store/slices/userSlice';
import { ROUTE_LOGIN } from '../../shared/variables/routes';

type Step = { key: string; done: boolean; action?: React.ReactNode; hint?: string };

/** Eski Android uygulamasından gelen oyuncuya üç adım: giriş, ana ekrana ekle, bildirimler. */
export default function AndroidAppWelcome() {
  const { t } = useTranslation('home');
  const { active, close } = useAndroidWelcome();
  const user = useAppSelector(selectUser);
  const install = useInstallApp();
  const push = usePush();
  const shown = useRef(false);
  const signedIn = !!user?.uid;
  const installed = install.path === 'installed';
  const pushReady = push.available && push.supported;

  useEffect(() => {
    if (!active || shown.current) return;
    shown.current = true;
    trackConversion('android_welcome_shown');
  }, [active]);

  if (!active) return null;

  const steps: Step[] = [
    {
      key: 'login',
      done: signedIn,
      action: (
        <Link href={`${ROUTE_LOGIN}?redirect=${encodeURIComponent('/')}`} passHref>
          <Button type="primary">{t('androidWelcome.loginAction')}</Button>
        </Link>
      ),
    },
    {
      key: 'install',
      done: installed,
      action: (
        <Button
          type={signedIn ? 'primary' : 'default'}
          onClick={() => {
            trackConversion('android_welcome_install');
            install.install();
          }}
        >
          {t('androidWelcome.installAction')}
        </Button>
      ),
    },
    ...(pushReady
      ? [
          {
            key: 'push',
            done: push.enabled,
            hint: !signedIn
              ? t('androidWelcome.pushNeedsLogin')
              : push.denied
              ? t('androidWelcome.pushDenied')
              : undefined,
            action: (
              <Button
                type={signedIn && installed ? 'primary' : 'default'}
                disabled={!signedIn || push.denied}
                loading={push.busy}
                onClick={() => {
                  trackConversion('android_welcome_push');
                  push.enable();
                }}
              >
                {t('androidWelcome.pushAction')}
              </Button>
            ),
          },
        ]
      : []),
  ];
  const finished = steps.every(step => step.done);

  return (
    <section className="android-welcome" aria-labelledby="android-welcome-title">
      <div className="android-welcome__head">
        <h2 id="android-welcome-title">
          {finished ? t('androidWelcome.doneTitle') : t('androidWelcome.title')}
        </h2>
        <Button
          type="text"
          className="android-welcome__close"
          icon={<CloseOutlined aria-hidden="true" />}
          aria-label={t('androidWelcome.close')}
          onClick={close}
        />
      </div>
      <p className="android-welcome__lead">
        {finished ? t('androidWelcome.doneText') : t('androidWelcome.text', { count: steps.length })}
      </p>
      <ol className="android-welcome__steps">
        {steps.map((step, index) => (
          <li key={step.key} className={step.done ? 'is-done' : undefined}>
            <span className="android-welcome__index" aria-hidden="true">
              {step.done ? <CheckCircleFilled /> : index + 1}
            </span>
            <span className="android-welcome__label">
              <strong>{t(`androidWelcome.step.${step.key}`)}</strong>
              {step.done ? (
                <span className="android-welcome__status">{t('androidWelcome.stepDone')}</span>
              ) : (
                step.hint && <span className="android-welcome__status">{step.hint}</span>
              )}
            </span>
            {!step.done && <span className="android-welcome__action">{step.action}</span>}
          </li>
        ))}
      </ol>
    </section>
  );
}
